import {
    useCallback,
    useMemo,
} from 'react'
import omit from 'lodash/omit'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    REDIRECT_PARAM,
} from 'app/shared-components/SkyNetTable/hooks/useAddCopiedItemWorkaround'

import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import LaneGroupRequests from 'app/requests/ContractBasis/LaneGroup/LaneGroup.requests'
import WarningCard from 'app/shared-components/WarningCard'
import useGetLaneGroup from '../hooks/useGetLaneGroup'
import LaneGroupForm from '../LaneGroupForm'

type Props = {
    id: number
}

const LaneGroupCopy = ({
    id,
}: Props) => {
    const {
        LaneGroups: LaneGroupsRoutes,
    } = usePricingRoutes()
    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetLaneGroup(id)

    const value = useMemo(() => {
        return {
            ...omit(data, [
                'id',
                'changedBy',
                'changedOn',
                'laneGroupNumber',
            ]),
            originId: data?.id,
        }
    }, [data])

    const onSuccess = useCallback(({
        id: copyId,
    }) => {
        invalidate()
        LaneGroupsRoutes.Overview.navigate({
            params: {
                key: copyId,
            },
            options: {
                state: REDIRECT_PARAM,
            },
        })
    }, [
        LaneGroupsRoutes.Overview,
        invalidate,
    ])

    const onCancel = useCallback(() => {
        LaneGroupsRoutes.Overview.navigate({
            params: {
                key: id,
            },
        })
    }, [
        LaneGroupsRoutes.Overview,
        id,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <WarningCard>
                    You are deep copying a lane group.
                    All awarded/implemented lanes and most recent pricing will be copied as well.
                </WarningCard>
                <LaneGroupForm
                    data={value}
                    onSuccess={onSuccess}
                    onCancel={onCancel}
                    requestParam={LaneGroupRequests.duplicate({
                        id: value?.originId,
                    })}
                />
            </>
        </StatusHandler>
    )
}

export default LaneGroupCopy
